<template>
    <div class="row g-3">
        <!-- Left Column: General Info -->
        <div class="col-lg-6">
            <!-- General Info Section -->
            <div class="meta-section-box mb-3">
                <div class="meta-section-header">
                    <span class="meta-header-icon meta-header-icon--blue">
                        <i class="bi bi-file-earmark-text-fill"></i>
                    </span>
                    <h6 class="mb-0 fw-bold meta-header-title">Ümumi məlumat</h6>
                </div>
                <div class="meta-content-body">
                    <div class="meta-row">
                        <span class="meta-label">Müqavilə ID</span>
                        <span class="meta-value font-monospace fw-bold text-primary">{{ data.policyId }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Müqavilə tarixi</span>
                        <span class="meta-value">{{ data.contractDateStr }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Müqavilə nömrəsi</span>
                        <span class="meta-value font-monospace fw-bold">{{ data.contractFullNumber }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Başlanma tarixi</span>
                        <span class="meta-value">{{ data.startDate }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Bitmə tarixi</span>
                        <span class="meta-value">{{ data.endDate }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Qeydlər</span>
                        <span class="meta-value text-muted">{{ data.descr || '—' }}</span>
                    </div>
                    <div class="meta-row border-bottom-0">
                        <span class="meta-label">Əlavə şərtlər</span>
                        <span class="meta-value">
                            <span class="meta-chip meta-chip--green">{{ data.addCond || '—' }}</span>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Responsible Person -->
            <div class="meta-section-box">
                <div class="meta-section-header">
                    <span class="meta-header-icon meta-header-icon--purple">
                        <i class="bi bi-person-badge-fill"></i>
                    </span>
                    <h6 class="mb-0 fw-bold meta-header-title">Məsul şəxs</h6>
                </div>
                <div class="meta-content-body">
                    <div class="meta-row">
                        <span class="meta-label">Tam adı</span>
                        <span class="meta-value fw-bold d-flex align-items-center gap-2">
                            <span class="person-mini-avatar avatar--blue">{{ getInitials(data.resp?.name) }}</span>
                            <span>{{ data.resp?.name }}</span>
                        </span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Telefon</span>
                        <span class="meta-value">
                            <a :href="'tel:' + data.resp?.phone" class="phone-meta-link">
                                <i class="bi bi-telephone me-1"></i>{{ data.resp?.phone }}
                            </a>
                        </span>
                    </div>
                    <div class="meta-row border-bottom-0">
                        <span class="meta-label">Ünvan</span>
                        <span class="meta-value text-muted">{{ data.resp?.address }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right Column: Insured Company & Status History -->
        <div class="col-lg-6">
            <!-- Insured Company Section -->
            <div class="meta-section-box mb-3">
                <div class="meta-section-header">
                    <span class="meta-header-icon meta-header-icon--green">
                        <i class="bi bi-building-fill"></i>
                    </span>
                    <h6 class="mb-0 fw-bold meta-header-title">Şirkət (Sığortalı)</h6>
                </div>
                <div class="meta-content-body">
                    <div class="meta-row">
                        <span class="meta-label">Şirkətin tam adı</span>
                        <span class="meta-value fw-bold text-dark-emphasis">{{ data.insured?.name }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">VÖEN</span>
                        <span class="meta-value">
                            <span class="font-monospace fw-bold text-info">{{ data.insured?.voen }}</span>
                        </span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Biznes sektoru</span>
                        <span class="meta-value">
                            <span class="meta-chip meta-chip--amber">{{ data.insured?.clientSector }}</span>
                        </span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Telefon</span>
                        <span class="meta-value">
                            <a :href="'tel:' + data.insured?.phone" class="phone-meta-link">
                                <i class="bi bi-telephone me-1"></i>{{ data.insured?.phone }}
                            </a>
                        </span>
                    </div>
                    <div class="meta-row border-bottom-0">
                        <span class="meta-label">Hüquqi ünvan</span>
                        <span class="meta-value text-muted" style="line-height: 1.35;">{{ data.insured?.address }}</span>
                    </div>
                </div>
            </div>

            <!-- Status History Timeline Table -->
            <div class="meta-section-box">
                <div class="meta-section-header">
                    <span class="meta-header-icon meta-header-icon--amber">
                        <i class="bi bi-clock-history"></i>
                    </span>
                    <h6 class="mb-0 fw-bold meta-header-title">Status və Təsdiq Tarixçəsi</h6>
                </div>
                <div class="p-2">
                    <el-table border stripe :data="statusData" class="el-table-css" style="width: 100%;">
                        <el-table-column prop="status" label="Status" width="160">
                            <template #default="scope">
                                <span class="status-badge-pill" :class="getStatusBadgeClass(scope.row.statusId)">
                                    {{ scope.row.status }}
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="user" label="Dəyişən şəxs" min-width="140">
                            <template #default="scope">
                                <span class="d-flex align-items-center gap-1">
                                    <i class="bi bi-person-fill text-muted"></i>
                                    <span>{{ scope.row.user }}</span>
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="time" label="Tarix" width="140" align="center">
                            <template #default="scope">
                                <span class="task-time-chip font-monospace">{{ scope.row.time }}</span>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ContractInfoData",
    props: {
        data: {
            type: Object,
            default: () => ({})
        },
        statusData: {
            type: Array,
            default: () => []
        }
    },
    methods: {
        getInitials(name) {
            if (!name) return "RM";
            const parts = name.trim().split(/\s+/);
            if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
            return (parts[0][0] + parts[1][0]).toUpperCase();
        },
        getStatusBadgeClass(statusId) {
            if (statusId === 0 || statusId === "0") return "badge--in-progress";
            if (statusId === 2 || statusId === "2") return "badge--approved";
            if (statusId === 4 || statusId === "4") return "badge--in-progress";
            return "badge--rejected";
        }
    }
};
</script>

<style scoped lang="scss">
.meta-section-box {
    background: var(--app-surface-soft, #f8fafc);
    border: 1px solid var(--app-border, #e2e8f0);
    border-radius: var(--app-radius, 8px);
    overflow: hidden;
    transition: all 0.15s ease;
}

.meta-section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: var(--app-surface, #ffffff);
    border-bottom: 1px solid var(--app-border, #e2e8f0);
}

.meta-header-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.88rem;

    &--blue {
        background: rgba(11, 79, 156, 0.12);
        color: var(--bs-info, #0b4f9c);
    }
    &--purple {
        background: rgba(147, 51, 234, 0.12);
        color: #9333ea;
    }
    &--green {
        background: rgba(16, 185, 129, 0.12);
        color: #059669;
    }
    &--amber {
        background: rgba(245, 158, 11, 0.12);
        color: #d97706;
    }
}

.meta-header-title {
    font-size: 0.88rem;
    color: var(--app-heading, #1e293b);
}

.meta-content-body {
    padding: 4px 14px;
}

.meta-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid var(--app-border, #e2e8f0);
    font-size: 0.82rem;
}

.meta-label {
    color: var(--app-text, #64748b);
    font-weight: 550;
    min-width: 140px;
    flex-shrink: 0;
}

.meta-value {
    color: var(--app-heading, #1e293b);
    text-align: right;
    word-break: break-word;
}

.meta-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: 0.74rem;
    font-weight: 700;

    &--blue {
        background: rgba(11, 79, 156, 0.1);
        color: var(--bs-info, #0b4f9c);
    }
    &--green {
        background: rgba(16, 185, 129, 0.12);
        color: #059669;
    }
    &--amber {
        background: rgba(245, 158, 11, 0.14);
        color: #b45309;
    }
}

.person-mini-avatar {
    width: 24px;
    height: 24px;
    border-radius: 5px;
    background: rgba(147, 51, 234, 0.14);
    color: #7c3aed;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 800;

    &.avatar--blue {
        background: rgba(37, 99, 235, 0.14);
        color: #2563eb;
    }
}

.phone-meta-link {
    color: var(--bs-info, #0b4f9c);
    text-decoration: none;
    font-weight: 600;

    &:hover {
        text-decoration: underline;
    }
}

.task-time-chip {
    font-size: 0.76rem;
    font-weight: 650;
    padding: 2px 6px;
    border-radius: 4px;
    background: var(--app-surface, #ffffff);
    border: 1px solid var(--app-border, #e2e8f0);
}

.status-badge-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: 0.72rem;
    font-weight: 700;
    white-space: nowrap;

    &.badge--approved {
        background: rgba(16, 185, 129, 0.12);
        color: #059669;
        border: 1px solid rgba(16, 185, 129, 0.35);
    }
    &.badge--in-progress {
        background: rgba(59, 130, 246, 0.12);
        color: #2563eb;
        border: 1px solid rgba(59, 130, 246, 0.35);
    }
    &.badge--rejected {
        background: rgba(239, 68, 68, 0.12);
        color: #dc2626;
        border: 1px solid rgba(239, 68, 68, 0.35);
    }
}

[data-theme="dark"] {
    .meta-section-box {
        background: #141b22;
        border-color: #283544;
    }

    .meta-section-header {
        background: #182029;
        border-color: #283544;
    }

    .meta-header-title {
        color: #f1f5f9;
    }

    .meta-row {
        border-color: #242f3d;
    }

    .meta-label {
        color: #94a3b8;
    }

    .meta-value {
        color: #f1f5f9;
    }

    .task-time-chip {
        background: #182029;
        border-color: #283544;
        color: #e2e8f0;
    }

    .status-badge-pill {
        &.badge--approved {
            background: rgba(16, 185, 129, 0.2);
            color: #34d399;
            border-color: rgba(52, 211, 153, 0.35);
        }
        &.badge--in-progress {
            background: rgba(59, 130, 246, 0.2);
            color: #93c5fd;
            border-color: rgba(96, 165, 250, 0.35);
        }
        &.badge--rejected {
            background: rgba(239, 68, 68, 0.2);
            color: #fca5a5;
            border-color: rgba(248, 113, 113, 0.35);
        }
    }
}
</style>